import TruncatedTypography from '@components/TruncatedTypography';
import { useLazyQuery } from '@apollo/client';
import { CollectionType } from '@/__generated__/graphql';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Box,
  Chip,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState, type FC, type MouseEvent } from 'react';
import GET_COLLECTION_DEVICES from '../graphql/queries/getCollectionDevices';

interface Props {
  style?: React.CSSProperties;
  lastItem?: boolean;
  collectionId: number;
  collectionName: string;
  collectionType: CollectionType;
  organizationName?: string | null;
  onClick?: () => void;
  hasAccess?: boolean;
  modified?: boolean;
  /** AMARU-562: asignada por un admin fuera de la(s) organización(es) del caller — visible, no editable. */
  locked?: boolean;
}

const CollectionsListItem: FC<Props> = ({
  lastItem,
  style,
  collectionId,
  collectionName,
  collectionType,
  organizationName,
  onClick,
  hasAccess: checked,
  modified,
  locked,
}) => {
  const { _ } = useLingui();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loadDevices, { data, loading }] = useLazyQuery(GET_COLLECTION_DEVICES, {
    variables: { collectionId },
  });

  const onNameClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    loadDevices();
  };

  const devices = data?.collection.devices ?? [];

  return (
    <ListItem
      disablePadding
      style={style}
      sx={{
        borderBottom: lastItem ? null : '1px solid',
        borderColor: 'divider',
      }}
    >
      <ListItemButton
        onClick={locked ? undefined : onClick}
        disabled={locked}
        sx={{ height: '100%', width: '100%', display: 'flex' }}
      >
        <Box width="calc(100% - 125px)" display="flex" alignItems="center" gap={2}>
          <TruncatedTypography fontSize={14} maxWidth="45%" onClick={onNameClick} sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
            {collectionName}
          </TruncatedTypography>
          <Chip label={collectionType} size="small" variant="outlined" />
          {organizationName && (
            <TruncatedTypography fontSize={12} maxWidth="30%" color="text.secondary">
              {organizationName}
            </TruncatedTypography>
          )}
        </Box>
        <Box display="flex" flex={1} justifyContent="right" alignItems="center" gap={1}>
          {locked && (
            <Tooltip title={_(msg`Compartida por otra organización — asignada por un administrador; no editable.`)}>
              <Typography fontSize="small" fontStyle="italic" color="text.secondary">
                <Trans>(compartida)</Trans>
              </Typography>
            </Tooltip>
          )}
          {modified && (
            <Typography fontSize="small" fontStyle="italic" color="text.secondary">
              <Trans>(editado)</Trans>
            </Typography>
          )}
          {checked != null && (
            <ListItemIcon>
              <Checkbox edge="end" checked={checked} disabled={locked} tabIndex={-1} />
            </ListItemIcon>
          )}
        </Box>
      </ListItemButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box p={1} minWidth={220} maxWidth={320}>
          <Typography variant="subtitle2" mb={1}>
            <Trans>Dispositivos</Trans>
          </Typography>
          {loading && <CircularProgress size={20} />}
          {!loading && devices.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              <Trans>Sin dispositivos</Trans>
            </Typography>
          )}
          {!loading && devices.length > 0 && (
            <List dense disablePadding>
              {devices.map((device) => (
                <ListItem key={device.id} disablePadding>
                  <Typography variant="body2">
                    {device.id} - {device.profile.name}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </ListItem>
  );
};

export default CollectionsListItem;
